import * as React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Paper, Box, Typography, Stack, Chip, Skeleton,
  Modal, Button, Divider, useTheme, Alert as MuiAlert
} from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import SmallConfigTemperatur from '../components/SmallConfigTemperatur';
import SmallConfigLicht from '../components/SmallConfigLicht';
import SmallConfigAbluft from '../components/SmallConfigAbluft';
import SmallConfigCo2 from './SmallConfigCo2';
import { useGrowSetup } from '../GrowSetupContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from './signin/AuthContext';

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = { color: PropTypes.string.isRequired, id: PropTypes.string.isRequired };

function getDeviationColor(value, ideal, theme) {
  const v = Number(value);
  const t = Number(ideal);
  if (!isFinite(v) || !isFinite(t) || t === 0) {
    return { color: theme.palette.secondary.main, text: 'secondary.main', level: 'unknown' };
  }
  const dev = Math.abs(v - t) / t;
  if (dev >= 0.2) return { color: theme.palette.error.main, text: 'error.main', level: 'bad' };
  if (dev >= 0.1) return { color: theme.palette.warning.main, text: 'warning.main', level: 'warn' };
  return { color: theme.palette.success.main, text: 'success.main', level: 'ok' };
}

function getTrendMeta(trend, theme) {
  switch (trend) {
    case 'up': return { color: theme.palette.success.main, Icon: ArrowUpwardRoundedIcon, label: 'steigend' };
    case 'down': return { color: theme.palette.error.main, Icon: ArrowDownwardRoundedIcon, label: 'fallend' };
    default: return { color: theme.palette.text.secondary, Icon: HorizontalRuleRoundedIcon, label: 'stabil' };
  }
}

function StatCard(props) {
  const theme = useTheme();
  const { grow_setup } = useGrowSetup();
  const { token } = useContext(AuthContext);

  const [datas, setData] = useState([]);
  const [xaxis, setXaxis] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  const deviation = getDeviationColor(props.gauge_value, props.ideal_val, theme);
  const trendMeta = getTrendMeta(props.trend, theme);
  const gradientId = useMemo(() => `area-gradient-${props.measure}-${props.value}`, [props.measure, props.value]);

  const styleModal = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420,
    maxWidth: '90vw',
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 2,
  };

  const valueFormatter = (value) => (value == null ? '' : Number(value).toFixed(2));

  const fetchData = async (measure) => {
    if (!grow_setup) return;
    setIsLoading(true);
    setErr(null);

    // 24h Range (lokal)
    const end = new Date();
    const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);

    const controller = new AbortController();
    try {
      const res = await axios.post(
        '/get_measures_by_timedelta/',
        {
          start_time: start,
          end_time: end,
          grow_setup_id: grow_setup,
          aggregation_level: '5m',
          measure,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );

      const row = res?.data?.[0];
      const data = row?.data ?? [];
      const rawX = row?.xaxis ?? [];

      // Schönere X-Achse (Zeit HH:MM)
      const fmt = (ts) => {
        const dt = new Date(ts);
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      };

      setData(data);
      setXaxis(rawX.map(fmt));
    } catch (e) {
      if (axios.isCancel?.(e)) return;
      console.error(e);
      setErr('Daten konnten nicht geladen werden.');
      setData([]);
      setXaxis([]);
    } finally {
      setIsLoading(false);
    }
    return () => controller.abort();
  };

  useEffect(() => {
    fetchData(props.measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grow_setup, props.measure]);

  return (
    <Paper
      elevation={theme.palette.mode === 'light' ? 8 : 1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        // dezente Statusfärbung:
        boxShadow: `inset 0 0 0 2px ${deviation.color}22`,
        background:
          theme.palette.mode === 'light'
            ? `${deviation.color}0D`
            : `${deviation.color}26`,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle2" color="text.secondary">
              {props.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={
                  deviation.level === 'bad'
                    ? 'zu stark abweichend'
                    : deviation.level === 'warn'
                    ? 'leicht abweichend'
                    : 'im Zielbereich'
                }
                color={
                  deviation.level === 'bad'
                    ? 'error'
                    : deviation.level === 'warn'
                    ? 'warning'
                    : 'success'
                }
                variant="outlined"
              />
            </Stack>
          </Stack>

          <Stack alignItems="flex-end" spacing={0.25}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                {props.gauge_value == null || props.gauge_value === 0
                  ? 'N/A'
                  : `${props.gauge_value}${props.einheit_zeichen ?? ''}`}
              </Typography>
              <trendMeta.Icon fontSize="small" sx={{ color: trendMeta.color }} />
            </Stack>
            {props.ideal_val != null && (
              <Typography variant="caption" color="text.secondary">
                Ziel: {props.ideal_val}{props.einheit_zeichen ?? ''}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ opacity: 0.5 }} />

      {/* Body / Chart */}
      <Box sx={{ px: 2, py: 1.5, flex: 1, minHeight: 90 }}>
        {isLoading ? (
          <Skeleton variant="rounded" height={60} />
        ) : err ? (
          <MuiAlert severity="error" variant="outlined">{err}</MuiAlert>
        ) : (
          <Box sx={{ width: '100%', height: 60 }}>
            <SparkLineChart
              data={datas}
              xAxis={{ scaleType: 'band', data: xaxis }}
              colors={[trendMeta.color]}
              valueFormatter={valueFormatter}
              area
              showHighlight
              showTooltip
              sx={{
                [`& .${areaElementClasses.root}`]: {
                  fill: `url(#${gradientId})`,
                },
                // dezente Achsen/Grids abschalten
                '& .MuiChartsAxis-root': { display: 'none' },
              }}
            >
              <AreaGradient color={trendMeta.color} id={gradientId} />
            </SparkLineChart>
          </Box>
        )}
      </Box>

      <Divider sx={{ mt: 'auto', opacity: 0.5 }} />

      {/* Actions */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={() => setOpen(true)}>Details anzeigen</Button>
      </Box>

      {/* Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={styleModal}>
          {props.measure === 'licht' && <SmallConfigLicht />}
          {props.measure === 'humidity' && <SmallConfigAbluft />}
          {props.measure === 'temperatur' && <SmallConfigTemperatur />}
          {props.measure === 'co2' && <SmallConfigCo2 />}
        </Box>
      </Modal>
    </Paper>
  );
}

StatCard.propTypes = {
  value: PropTypes.string.isRequired,               // nur für Chart-ID/Key
  measure: PropTypes.string.isRequired,             // 'temperatur' | 'humidity' | 'licht' | 'co2' ...
  title: PropTypes.string,                          // optional: Anzeigename
  trend: PropTypes.oneOf(['down', 'neutral', 'up']).isRequired,
  ideal_val: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gauge_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  einheit_zeichen: PropTypes.string,
};
export default React.memo(StatCard);
